import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ServiceResponse } from 'src/app/@shared/interfaces/service-response.interface';
import { LogService } from 'src/app/@shared/services/log/log.service';
import { Address } from 'src/app/modules/route-planner/interfaces/location/address.interface';
import { RoutePlanning } from 'src/app/modules/route-planner/interfaces/route-planning/@route-planning.interface';
import { LocationService } from 'src/app/modules/route-planner/services/location/location.service';
import { MapService } from 'src/app/modules/route-planner/services/map/map.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { RoutePlannerFormComponent } from '../../route-planner-form.component';
import { SourceDestinationForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/source-destination-form';
import { eDepartureTimeOptions } from 'src/app/modules/route-planner/constants/departure-time-options.constants';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import { SourceDestinationFiltersForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/filters/source-destination-filters-form';
import { LocalStorageService } from 'src/app/@shared/services/local-storage/local-storage.service';
import { FormValidators } from 'src/app/@forms/validators/validators';
import { ActivatedRoute, Router } from '@angular/router';
import { RouteFiltersComponent } from './route-filters/route-filters.component';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { eTransportationOptions } from 'src/app/modules/route-planner/constants/transportation-options.constants';
import { eSortOptions } from 'src/app/modules/route-planner/constants/sort-options.constants';
import { SourceDestinationResultsContainerComponent } from 'src/app/modules/route-planner/containers/route-planning/route-planning-results-container/source-destination-results-container/source-destination-results-container.component';
import { AutoCompleteService } from 'src/app/modules/route-planner/services/auto-complete/auto-complete.service';
import { AddressAutoCompleteComponent } from './address-auto-complete/address-auto-complete.component';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  ADD_STOP,
  CLOSE,
  DELETE_STOP,
  DESTINATION,
  FILTERING_AND_SORTING,
  FIND_A_ROUTE,
  ORIGIN,
  PICK_STOP,
  ROUTE_PLANNING,
  SEARCH_DESTINATION,
  SEARCH_ORIGIN,
  SWITCH_LOCATIONS,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { Direction } from '@angular/cdk/bidi';
import { SourceDestinationQueryParams } from 'src/app/modules/route-planner/interfaces/route-planning/query-params/source-destination-query-params.interface';
import { FilterStateService, initialTransportationsValues } from 'src/app/modules/route-planner/services/filters/filter-state.service';
import { Subscription, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Section } from 'src/app/modules/route-planner/interfaces/route-planning/section.interface';
import { NextStopTime } from 'src/app/modules/route-planner/interfaces/route-planning/next-stop-times.interface';
import { IntermediateStop } from 'src/app/modules/route-planner/interfaces/route-planning/intermediate-stop.interface';
import { MotAutocompleteComponent } from 'src/app/@forms/components/mot-autocomplete/mot-autocomplete.component';
import { PlannerSurveyOrchestratorService } from 'src/app/modules/route-planner/services/survey/planner-survey-orchestrator.service';

@Component({
  selector: 'app-source-destination-form',
  templateUrl: './source-destination-form.component.html',
  styleUrls: ['./source-destination-form.component.scss'],
  standalone: false
})
export class SourceDestinationFormComponent
  extends RoutePlannerFormComponent
  implements OnInit {
  @ViewChild('stopMotAutocomplete')
  stopMotAutocomplete?: MotAutocompleteComponent<Address>;

  private readonly initialSourceDestinationFormValues = {
    origin: null,
    destination: null,
    stop: null,
    timeOpt: eDepartureTimeOptions.NOW,
    arrival: {
      date: new Date(),
      time: this.utilsService.getCurrentTime()
    },
    departure: {
      date: new Date(),
      time: this.utilsService.getCurrentTime()
    },
    options: {
      isAccessible: false,
      changes: true,
      transportations: [...initialTransportationsValues],
      sortBy: eSortOptions.FREQUENCY_AND_TRAVEL_TIME,
      maxTravelTime: 600,
      maxWalkTime: 120,
    }
  };

  // stopAddresses: Observable<SelectOption<Address>[]>[] = []; Keeping in case we will go back to multiple stops

  @Output() toggleSearchButton: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() toogleLoader: EventEmitter<boolean> = new EventEmitter<boolean>();

  showStopInput: boolean = false;
  resubmittedWithoutFilters: boolean = false;
  appliedFilters: boolean = false;
  isLoading: boolean = false;

  originText: string;
  searchOriginText: string;
  destinationText: string;
  searchDestinationText: string;
  addStopText: string;
  closeText: string;
  /**
   * a11y: aria-label for the trash-icon button next to the stop (waypoint)
   * input. SR users previously heard "סגור" (close) which doesn't reflect
   * what the button actually does — it removes the added stop entry. The
   * label now reads "מחיקת עצירה" / "Delete stop" / "حذف التوقف".
   */
  deleteStopText: string;
  pickStopText: string;
  filterText: string;
  switchLocationsText: string = SWITCH_LOCATIONS.hebrew;

  direction: Direction;

  isInitializing = true;

  // Change appliedFilters to be an Observable
  appliedFilters$ = this.filterStateService.currentFilters$.pipe(
    map((filters) => !this.filterStateService.filtersAreDefault(filters))
  );

  constructor(
    private readonly modalService: ModalService,
    private readonly localStorageService: LocalStorageService,
    private readonly autoCompleteService: AutoCompleteService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly utilsService: UtilsService,
    private readonly languageService: LanguageService,
    private readonly filterStateService: FilterStateService,
    private readonly plannerSurveyOrchestrator: PlannerSurveyOrchestratorService,

    queryParamsService: QueryParamsService,
    fb: FormBuilder,
    locationsService: LocationService,
    routePlanningService: RoutePlanningService,
    router: Router
  ) {
    super(
      eTab.SOURCE_DESTINATION,
      'מצא לי מסלול',
      fb,
      locationsService,
      queryParamsService,
      routePlanningService,
      router,
      utilsService
    );

    this.routePlannerForm = fb.group({
      ...this.routePlannerForm.controls,
      origin: new FormControl<Address>(this.initialSourceDestinationFormValues.origin, FormValidators.required),
      destination: new FormControl<Address>(this.initialSourceDestinationFormValues.destination, FormValidators.required),
      stop: new FormControl<Address>(this.initialSourceDestinationFormValues.stop),
      // stops: new FormControl<Address[]>([]),
      timeOpt: new FormControl<eDepartureTimeOptions>(this.initialSourceDestinationFormValues.timeOpt),
      arrival: fb.group({
        date: new FormControl<Date>(this.initialSourceDestinationFormValues.arrival.date),
        time: new FormControl<string>(this.initialSourceDestinationFormValues.arrival.time, [FormValidators.sourceDestiationTimeValidator(() => this.routePlannerForm?.get('timeOpt')?.value,
          eDepartureTimeOptions.ARRIVAL, () => this.routePlannerForm?.get('arrival.date'))]),
      }),
      departure: fb.group({
        date: new FormControl<Date>(this.initialSourceDestinationFormValues.departure.date),
        time: new FormControl<string>(this.initialSourceDestinationFormValues.departure.time, [FormValidators.sourceDestiationTimeValidator(() => this.routePlannerForm?.get('timeOpt')?.value,
          eDepartureTimeOptions.DEPART, () => this.routePlannerForm?.get('departure.date'))]),
      }),
      options: fb.group({
        isAccessible: new FormControl<boolean>(this.initialSourceDestinationFormValues.options.isAccessible),
        changes: new FormControl<boolean>(this.initialSourceDestinationFormValues.options.changes),
        transportations: new FormControl<eTransportationOptions[]>(this.initialSourceDestinationFormValues.options.transportations),
        sortBy: new FormControl<eSortOptions>(
          this.initialSourceDestinationFormValues.options.sortBy
        ),
        maxTravelTime: new FormControl<number>(this.initialSourceDestinationFormValues.options.maxTravelTime),
        maxWalkTime: new FormControl<number>(this.initialSourceDestinationFormValues.options.maxWalkTime),
      }),
    });
  }

  private filterSubscription: Subscription;

  ngOnInit(): void {
    // Patch the saved filters into the form if they are not default.
    const currentFilters = this.filterStateService.getCurrentFilters();
    if (!this.filterStateService.filtersAreDefault(currentFilters)) {
      this.appliedFilters = true;
      // Patch the "options" control with the saved filters
      this.routePlannerForm.patchValue({ options: currentFilters });
    }

    // Subscribe to value changes to update query params etc.
    this.routePlannerForm.controls['origin'].valueChanges.subscribe((val: Address) => {
      this.setAddressQueryParam(val, 'org', 'oll')
    }
    );
    this.routePlannerForm.controls['destination'].valueChanges.subscribe(
      (val: Address) => this.setAddressQueryParam(val, 'dest', 'dll')
    );
    this.routePlannerForm.controls['stop'].valueChanges.subscribe((val: Address) => {
      this.setAddressQueryParam(val, 'stop', 'sll')
    }
    );

    // Sync address when user picks from the map overlay (one-shot Subject, no replay issues)
    this.routePlanningService.mapAddressPicked$.subscribe(({ field, address }) => {
      const control = this.routePlannerForm.get(field);
      if (!control) return;
      if (field === 'stop') {
        this.showStopInput = true;
        this.queryParamsService.setQueryParam('stopOpen', '1');
      }
      control.patchValue(address);
    });

    // Subscribe to filter state for dynamic changes
    this.filterSubscription = this.filterStateService.currentFilters$.subscribe(
      (filters) => {
        this.appliedFilters =
          !this.filterStateService.filtersAreDefault(filters);

      }
    );

    // Check if we're loading from URL params - if so, delay allowing resets until initialization completes
    const hasTimeParam = !!this.queryParamsService.currentParams['time'];
    if (hasTimeParam) {
      // Allow date-time picker to initialize from URL params first
      setTimeout(() => {
        this.isInitializing = false;
      }, 200);
    } else {
      this.isInitializing = false;
    }

    this.routePlannerForm.controls['timeOpt'].valueChanges.subscribe((value) => {
      if (this.isInitializing) {
        return;
      }

      const departureTime = this.routePlannerForm.get('departure.time');
      const arrivalTime = this.routePlannerForm.get('arrival.time');

      departureTime.setValue(this.utilsService.getCurrentTime())
      arrivalTime.setValue(this.utilsService.getCurrentTime())

    });

    this.routePlannerForm.get('arrival.date')?.valueChanges.subscribe(() => {
      this.routePlannerForm.get('arrival.time')?.updateValueAndValidity();
    });

    this.routePlannerForm.get('departure.date')?.valueChanges.subscribe(() => {
      this.routePlannerForm.get('departure.time')?.updateValueAndValidity();
    });


    this.initializeMultiLanguageTexts();
  }

  ngOnDestroy(): void {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.direction = language.direction;
      this.originText = ORIGIN[language.language];
      this.searchOriginText = SEARCH_ORIGIN[language.language];
      this.destinationText = DESTINATION[language.language];
      this.searchDestinationText = SEARCH_DESTINATION[language.language];
      this.filterText = FILTERING_AND_SORTING[language.language];
      this.addStopText = ADD_STOP[language.language];
      this.closeText = CLOSE[language.language];
      this.deleteStopText = DELETE_STOP[language.language];
      this.pickStopText = PICK_STOP[language.language];
      this.switchLocationsText = SWITCH_LOCATIONS[language.language];

      this.buttonTitle = FIND_A_ROUTE[language.language];
    });
    // this.appliedFilters = !!this.localStorageService.getItem('filters');//delete??
  }

  private setAddressQueryParam(
    address: Address,
    labelQueryParam: string,
    coordinatesQueryParam: string
  ) {
    const queryParam = {};

    queryParam[labelQueryParam] = address?.addressDetails?.label;
    queryParam[coordinatesQueryParam] = !!address
      ? `${address.coordinates.lat}_${address.coordinates.lng}`
      : null;

    this.queryParamsService.setQueryParams(queryParam);
  }

  private syncAddressFromQueryParam(
    queryParams: Record<string, unknown>,
    labelKey: string,
    coordsKey: string,
    formControlName: 'origin' | 'destination' | 'stop',
    beforePatch?: () => void
  ): void {
    const label = queryParams[labelKey] as string | undefined;
    const coordsStr = queryParams[coordsKey] as string | undefined;
    if (!label || !coordsStr) return;
    const parts = coordsStr.split('_');
    if (
      parts.length !== 2 ||
      isNaN(parseFloat(parts[0])) ||
      isNaN(parseFloat(parts[1]))
    ) {
      return;
    }
    const newLat = parseFloat(parts[0]);
    const newLng = parseFloat(parts[1]);
    const control = this.routePlannerForm.get(formControlName);
    const current = control?.value as Address;
    if (current?.coordinates?.lat === newLat && current?.coordinates?.lng === newLng) {
      return;
    }
    beforePatch?.();
    control?.patchValue({
      addressDetails: { label },
      coordinates: { lat: newLat, lng: newLng },
    });
  }

  //#region multiple stops
  // addStop() {
  //   this.stopAddresses.push(of([]));
  // }

  // removeStop(index: number) {
  //   const formValues: SourceDestinationForm = this.routePlannerForm.value;
  //   const stops = formValues.stops;

  //   this.stopAddresses.splice(index, 1);
  //   stops.splice(index, 1);
  // }
  //#endregion

  override onTabLeave() {
    const initialForm = { ...this.initialSourceDestinationFormValues }
    initialForm.options.transportations = [...initialTransportationsValues]
    const userFilters = this.filterStateService.getUserSavedFilters()
    if (userFilters) {
      initialForm.options = { ...userFilters }
    }
    this.routePlannerForm.reset(initialForm)
    this.filterStateService.clearFilters();
  }

  /**
   * @param focusAfterReveal When true (default), moves focus to the stop field for keyboard / SR users.
   * Set false when opening from URL/query sync so we don't steal focus on load.
   */
  openStop(focusAfterReveal = true): void {
    this.showStopInput = true;
    this.queryParamsService.setQueryParam('stopOpen', '1');
    if (focusAfterReveal) {
      setTimeout(() => this.stopMotAutocomplete?.focusInput(), 0);
    }
  }

  override autofillQueryParams() {
    const queryParams: SourceDestinationQueryParams = {
      ...this.queryParamsService.currentParams,
    };
    let shouldSubmitForm = false;

    if (!!queryParams.org && !!queryParams.oll) {
      const coordinates = queryParams.oll.split('_');
      // Validate coordinates before setting them
      if (
        coordinates.length === 2 &&
        !isNaN(parseFloat(coordinates[0])) &&
        !isNaN(parseFloat(coordinates[1]))
      ) {
        this.routePlannerForm.patchValue({
          origin: {
            addressDetails: {
              label: queryParams.org,
            },
            coordinates: {
              lat: parseFloat(coordinates[0]),
              lng: parseFloat(coordinates[1]),
            },
          },
        });
        shouldSubmitForm = true;
      } else {
        console.error(
          'Invalid origin coordinates in URL parameters:',
          queryParams.oll
        );
      }
    }

    if (!!queryParams.dest && !!queryParams.dll) {
      const coordinates = queryParams.dll.split('_');
      // Validate coordinates before setting them
      if (
        coordinates.length === 2 &&
        !isNaN(parseFloat(coordinates[0])) &&
        !isNaN(parseFloat(coordinates[1]))
      ) {
        this.routePlannerForm.patchValue({
          destination: {
            addressDetails: {
              label: queryParams.dest,
            },
            coordinates: {
              lat: parseFloat(coordinates[0]),
              lng: parseFloat(coordinates[1]),
            },
          },
        });
        shouldSubmitForm = true;
      } else {
        console.error(
          'Invalid destination coordinates in URL parameters:',
          queryParams.dll
        );
      }
    }

    if (!!queryParams.stop && !!queryParams.sll) {
      const coordinates = queryParams.sll.split('_');
      // Validate coordinates before setting them
      if (
        coordinates.length === 2 &&
        !isNaN(parseFloat(coordinates[0])) &&
        !isNaN(parseFloat(coordinates[1]))
      ) {
        this.openStop(false);
        this.routePlannerForm.patchValue({
          stop: {
            addressDetails: {
              label: queryParams.stop,
            },
            coordinates: {
              lat: parseFloat(coordinates[0]),
              lng: parseFloat(coordinates[1]),
            },
          },
        });
      } else {
        console.error(
          'Invalid stop coordinates in URL parameters:',
          queryParams.sll
        );
      }
    }

    // If both origin and destination are set from URL parameters, submit the form after a delay
    if (shouldSubmitForm && !!queryParams.org && !!queryParams.dest) {
      // Add a delay to ensure the application is fully initialized
      setTimeout(() => {
        this.submitRoutePlannerForm();
      }, 0);
    }
  }

  // addStop() {
  //   this.stopAddresses.push(of([]));
  // }

  closeStop() {
    this.routePlannerForm.patchValue({ stop: null });
    this.showStopInput = false;
    this.queryParamsService.setQueryParam('stopOpen', null);
  }

  override submitRoutePlannerForm() {
    if (!!this.routePlannerForm.valid) {
      const selectedTimeOpt = this.routePlannerForm.controls['timeOpt'].value;
      const routePlanningFormValues: SourceDestinationForm =
        this.routePlannerForm.value;

      // Log the form values for debugging


      // Additional validation for coordinates
      if (!this.validateCoordinates(routePlanningFormValues)) {
        console.error('Invalid coordinates in form values, cannot submit form');
        this.toggleSearchButton.emit(false);
        this.toogleLoader.emit(false);
        return;
      }

      // Set loading state to true before making the API call
      this.isLoading = true;
      this.routePlanningService.updateLoadingState(true);

      const routesObs$ = this.getRoutePlanningObs(
        routePlanningFormValues,
        selectedTimeOpt
      );

      routesObs$.subscribe({
        next: (routePlanningRes: ServiceResponse<RoutePlanning[]>) => {
          if (routePlanningRes.data && routePlanningRes.data.length > 0) {
            routePlanningRes.data.forEach((route: RoutePlanning) => {
              if (route.sections && route.sections.length > 0) {
                route.sections.forEach((section: Section) => {
                  if (section.departure && section.departure.nextStopsTimes && section.departure.nextStopsTimes.length > 0) {
                    section.departure.nextStopsTimes.forEach((stopTime: NextStopTime) => {
                      if (stopTime.isRealTime) {
                      }
                    });
                  }
                  if (section.intermediateStops && section.intermediateStops.length > 0) {
                    section.intermediateStops.forEach((stop: IntermediateStop) => {
                      stop.departure.time = this.utilsService.createGMTPlus2Date(stop.departure.time);
                    });
                  }
                })
              }
            })
          }
          if (routePlanningRes.data && routePlanningRes.data.length > 0) {
            this.handleSuccessfullResponse(
              routePlanningRes,
              routePlanningFormValues
            );
          } else {
            this.resendWithoutFilters(
              routePlanningFormValues.origin,
              routePlanningFormValues.destination,
              routePlanningFormValues.stop
            );
          }
        },
        error: (error) => {
          console.error('Error in route planning:', error);
          this.toggleSearchButton.emit(false);
          this.toogleLoader.emit(false);
          // Reset loading state on error
          this.isLoading = false;
          this.routePlanningService.updateLoadingState(false);
        },
        complete: () => {
          // Reset loading state when complete
          this.isLoading = false;
          this.routePlanningService.updateLoadingState(false);
        },
      });
    } else {
      console.warn('Form is invalid:', this.routePlannerForm.errors);
    }
  }

  private getRoutePlanningObs(
    routePlanningFormValues: SourceDestinationForm,
    selectedTimeOpt: any,
    isFirstSend: boolean = true
  ) {
    if (isFirstSend) {
      this.resubmittedWithoutFilters = false
    }
    this.toggleSearchButton.emit(true);
    this.toogleLoader.emit(true);
    const language = this.routePlanningService.getLanguageForRoute();


    // Validate coordinates before making the API call
    if (!this.validateCoordinates(routePlanningFormValues)) {
      console.error(
        'Invalid coordinates in form values:',
        routePlanningFormValues
      );
      // Return an observable that emits an empty result with the correct type
      return of<ServiceResponse<RoutePlanning[]>>({
        data: [],
        success: false,
        message: 'Invalid coordinates',
      });
    }
    const arrivalTime = this.routePlanningService.getArrival(
      selectedTimeOpt,
      routePlanningFormValues
    )

    const departureTime = this.routePlanningService.getDeparture(
      selectedTimeOpt,
      routePlanningFormValues
    )
    //LogService.logInfo(RoutePlanningForm values, routePlanningFormValues);
    return this.routePlanningService.getRoutesFromApi({
      route: {
        origin: routePlanningFormValues.origin,
        destination: routePlanningFormValues.destination,
        // stops: routePlanningFormValues.stops.map<Address>((stop) => stop),
        stop: routePlanningFormValues.stop,
        options: {
          ...routePlanningFormValues.options,
          transportations: this.routePlanningService.setTransportations(
            routePlanningFormValues
          ),
          transportationsForInitialUse: routePlanningFormValues.options.transportations,
          arrivalTime,
          departureTime,
        },
      },
      returnedRouteOptions: {
        language,
        isFutureSearch: (this.utilsService.calculateDiffInMinFromPresentTime(departureTime) > 60 || arrivalTime) ? true : false
      },
    });
  }

  // Helper method to validate coordinates
  private validateCoordinates(formValues: SourceDestinationForm): boolean {
    // Check if origin coordinates are valid
    if (
      !formValues.origin?.coordinates?.lat ||
      !formValues.origin?.coordinates?.lng
    ) {
      return false;
    }

    // Check if destination coordinates are valid
    if (
      !formValues.destination?.coordinates?.lat ||
      !formValues.destination?.coordinates?.lng
    ) {
      return false;
    }

    return true;
  }

  private handleSuccessfullResponse(
    routePlanningRes: ServiceResponse<RoutePlanning[]>,
    routePlanningFormValues: SourceDestinationForm
  ) {
    this.toggleSearchButton.emit(false);
    this.toogleLoader.emit(false);
    this.routePlannerForm.markAsPristine();
    if (!routePlanningRes.data) {
      return;
    }
    this.routePlanningService._formSubject.next({
      formValues: routePlanningFormValues,
      results: routePlanningRes.data,
      resubmittedWithoutFilters: this.resubmittedWithoutFilters,
    });

    this.routePlanningService._componentOverlaySub.next({
      componentType: SourceDestinationResultsContainerComponent,
      data: {
        closeCallbackFn: (resubmitForm) => {
          if (!!resubmitForm) {
            const currentFilters: SourceDestinationFiltersForm =
              this.filterStateService.getCurrentFilters();

            this.applyFilters(currentFilters);
            this.submitRoutePlannerForm();
          }
        },
        appliedFilters: this.appliedFilters,
      },
    });

    if (routePlanningRes.data.length > 0) {
      this.plannerSurveyOrchestrator.scheduleSurveyAfterSearch({
        plannerScenario: 'od',
        originLocationId: routePlanningFormValues.origin?.locationId,
        destinationLocationId: routePlanningFormValues.destination?.locationId,
      });
    }
  }

  private resendWithoutFilters(origin: Address, destination: Address, stop: Address = null) {
    this.resubmittedWithoutFilters = true;
    this.routePlannerForm.reset(
      {
        origin,
        destination,
        stop,
        timeOpt: this.initialSourceDestinationFormValues.timeOpt,
        arrival: {
          date: this.initialSourceDestinationFormValues.arrival.date,
          time: this.initialSourceDestinationFormValues.arrival.time,
        },
        departure: {
          date: this.initialSourceDestinationFormValues.departure.date,
          time: this.initialSourceDestinationFormValues.departure.time,
        },
        options: { ...this.routePlannerForm.controls['options'].value },
      },
      { emitEvent: true }
    );

    this.filterStateService.clearFilters()
    const routePlanningFormValues: SourceDestinationForm =
      this.routePlannerForm.value;

    routePlanningFormValues.options = { ...this.initialSourceDestinationFormValues.options, transportations: [...initialTransportationsValues] }
    // Keep the loading state true for the second API call
    this.isLoading = true;
    this.routePlanningService.updateLoadingState(true);

    const routesWithoutFiltersObs$ = this.getRoutePlanningObs(
      routePlanningFormValues,
      routePlanningFormValues.timeOpt,
      false
    );

    routesWithoutFiltersObs$.subscribe({
      next: (routePlanningRes: ServiceResponse<RoutePlanning[]>) => {
        this.handleSuccessfullResponse(
          routePlanningRes,
          routePlanningFormValues
        );
      },
      error: (error) => {
        console.error('Error in route planning without filters:', error);
        this.toggleSearchButton.emit(false);
        this.toogleLoader.emit(false);
        // Reset loading state on error
        this.isLoading = false;
        this.routePlanningService.updateLoadingState(false);
      },
      complete: () => {
        // Reset loading state when complete
        this.isLoading = false;
        this.routePlanningService.updateLoadingState(false);
      },
    });
  }

  drop(evt: CdkDragDrop<any, any, any>) {
    // const stops = this.routePlannerForm.controls['stops'].value;
    // moveItemInArray(stops, evt.previousIndex, evt.currentIndex);
    // moveItemInArray(this.stopAddresses, evt.previousIndex, evt.currentIndex);
  }

  openFilterMenu() {
    const filtersForm = this.routePlannerForm.controls['options'] as FormGroup;
    const currentTransportationValues = [...filtersForm.value.transportations]
    this.routePlanningService._componentOverlaySub.next({
      componentType: RouteFiltersComponent,
      data: {
        initialFilters: filtersForm.value,
        closeCallbackFn: (filtersResult) => {
          if (filtersResult.reOpen) {
            this.openFilterMenu();
          }
          else {
            if (filtersResult.applyFilters) {
              const routePlanningFiltersFormValue =
                filtersResult.value as SourceDestinationFiltersForm;

              this.applyFilters(routePlanningFiltersFormValue);
            } else {
              this.routePlannerForm.controls['options'].patchValue({ ...filtersForm.value, transportations: [...currentTransportationValues] })
            }
            if (filtersResult.isDefaultFilters) {
              this.appliedFilters = false;
            }
          }
        },
      },
    });
  }

  override openAutocomplete(
    addressFormControl: FormControl<Address>,
    title: string,
    target?: 'origin' | 'destination' | 'stop'
  ) {
    this.routePlanningService._componentOverlaySub.next({
      componentType: AddressAutoCompleteComponent,
      data: {
        title: title,
        address: addressFormControl.value,
        saveResultToStorage: true,
        hideCurrentLocation: target !== 'origin',
        closeCallbackFn: (address: Address) => addressFormControl.setValue(address),
      },
    });
  }

  private applyFilters(
    routePlanningFiltersFormValue: SourceDestinationFiltersForm
  ) {
    this.appliedFilters = !!routePlanningFiltersFormValue;
    this.routePlannerForm.patchValue(
      { options: routePlanningFiltersFormValue },
      {
        emitEvent: true,
      }
    );
    this.routePlannerForm.controls['options'].markAsDirty();
  }

  handleFavoriteRouteClick(favoriteRoute: {
    formValues: SourceDestinationForm;
    route: RoutePlanning;
  }) {
    this.routePlannerForm.patchValue({
      origin: favoriteRoute.formValues.origin,
      destination: favoriteRoute.formValues.destination,
    });

    this.routePlanningService.addAddressesToQueryParam([
      {
        queryParam: 'org',
        value: favoriteRoute.formValues.origin.addressDetails.label,
      },
      {
        queryParam: 'dest',
        value: favoriteRoute.formValues.destination.addressDetails.label,
      },
    ]);
  }
}
function futureTimeValidator(): import("@angular/forms").ValidatorFn {
  throw new Error('Function not implemented.');
}

